import lodashDifference from 'lodash.difference';

import MessagingServiceManager from './manager';
import type { AtomicSubscriber, Criteria, MessagePayload } from './types';

/**
 * @ignore
 * Module enables one subscription to websocket and enables
 * many components to subscribe to.
 * Example:
 * Case updates like adding new file, followers etc.,
 */
class AtomicSubscription {
  private atomicSubscribers: { [subId: string]: AtomicSubscriber };
  private connectionsCount: number;
  private connected: boolean;
  private readonly matcher: string;
  private readonly criteria: Criteria;
  private messagingServiceSubscriptionId: string;

  /*
   * @description
   * Initialization
   * @param matcher eq., CASE
   * @param criteria eq., {
   *  caseId: "Approval-Request A-101"
   * }
   */
  constructor(matcher: string, criteria: Criteria) {
    /**
     * object structure:
     * {
     *  "<subId>": [
     *  {
     *      criteria: {
     *      },
     *      callback: fn
     *  }
     * ]
     * }
     */
    this.atomicSubscribers = {};
    this.connectionsCount = 1;
    this.connected = false;
    this.matcher = matcher;
    this.criteria = criteria;
    this.messagingServiceSubscriptionId = '';
  }

  /*
   * @description
   * handles messages
   * @param message.message
   * example:
   * {
   *  filter: {
   *      matcher: "CASE",
   *      criteria: {
   *          caseId: "Approval-Request A-101"
   *      }
   *  },
   *  message: {
   *      "attachmentUpdated": true
   *  }
   * }
   *
   * If any message properties matches to criteria then
   * callbacks will be invoked.
   */
  _handleAtomicMessage({ message }: MessagePayload) {
    Object.keys(this.atomicSubscribers).forEach((subId) => {
      const subscription = this.atomicSubscribers[subId];
      if (!subscription) return;
      const subCriteriaKeys = Object.keys(subscription.criteria);
      const messageKeys = Object.keys(message);

      if (lodashDifference(subCriteriaKeys, messageKeys).length === 0) {
        subscription.callback(message);
      }
    });
  }

  _getAutoGenId() {
    this.connectionsCount += 1;
    return `${Date.now()}_${this.connectionsCount}`;
  }

  /*
   * @description
   * Register to events for specific object.
   * @param criteria example {attachmentUpdated: true}
   * @param callback
   * @returns
   */
  subscribe(criteria: Criteria, callback: Function) {
    const subId = this._getAutoGenId();

    // Only create a messaging service subscription once. Use messagingServiceSubscriptionId
    // as the definitive guard so we don't accidentally subscribe twice due to races.
    if (!this.messagingServiceSubscriptionId) {
      // set a temporary id immediately to prevent race where two callers both
      // pass the above check before we assign the real id returned by manager.
      // We optimistically reserve our generated id so subsequent calls will
      // observe a truthy messagingServiceSubscriptionId and skip subscribing.
      this.messagingServiceSubscriptionId = subId;
      const subscription = {
        matcher: this.matcher,
        criteria: this.criteria
      };
      // Call manager and prefer its returned id when available. Fall back to our generated id.
      const returnedId = MessagingServiceManager.subscribe(
        subscription,
        this._handleAtomicMessage.bind(this),
        undefined,
        subId
      );

      // Overwrite the temporary id with the manager returned id when provided.
      this.messagingServiceSubscriptionId = returnedId || subId;
      this.connected = true;
    }

    this.atomicSubscribers[subId] = {
      criteria,
      callback
    };

    return subId;
  }

  unsubscribe(subscriptionId: string) {
    delete this.atomicSubscribers[subscriptionId];

    if (Object.keys(this.atomicSubscribers).length === 0) {
      this.connected = false;
      if (this.messagingServiceSubscriptionId) {
        MessagingServiceManager.unsubscribe(this.messagingServiceSubscriptionId);
        // Clear the stored subscription id so future subscribe() calls will re-subscribe.
        this.messagingServiceSubscriptionId = '';
      }
    }
  }
}

export default AtomicSubscription;
